import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR, API } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { StatusBadge } from "../components/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Globe, Star, MagnifyingGlass, ArrowSquareOut, Tag, Wrench, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WebsiteControl() {
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ categories: [], service_types: [] });
  const [preview, setPreview] = useState([]);
  const [search, setSearch] = useState("");

  const loadAll = useCallback(() => {
    api.get("/website/services").then((r) => setRows(r.data));
    api.get("/website/config").then((r) => setConfig(r.data));
    api.get("/public/services").then((r) => setPreview(r.data));
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshPreview = () => api.get("/public/services").then((r) => setPreview(r.data));

  const patchRow = async (rid, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === rid ? { ...r, [field]: value } : r)));
    try {
      await api.put(`/website/services/${rid}`, { [field]: value });
      toast.success("Updated");
      refreshPreview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); loadAll(); }
  };

  const patchPayment = async (rid, payment_mode, booking_amount) => {
    setRows((prev) => prev.map((r) => (r.id === rid ? { ...r, payment_mode, booking_amount } : r)));
    try {
      const body = payment_mode === "partial" ? { payment_mode, booking_amount: Number(booking_amount) || 0 } : { payment_mode };
      const { data } = await api.put(`/website/services/${rid}`, body);
      setRows((prev) => prev.map((r) => (r.id === rid ? { ...r, ...data } : r)));
      toast.success("Payment mode updated");
      refreshPreview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); loadAll(); }
  };

  const toggleConfig = async (kind, name, visible) => {
    const key = kind === "category" ? "categories" : "service_types";
    const updated = config[key].map((x) => (x.name === name ? { ...x, visible } : x));
    setConfig({ ...config, [key]: updated });
    const body = kind === "category"
      ? { visible_categories: updated.filter((x) => x.visible).map((x) => x.name) }
      : { visible_service_types: updated.filter((x) => x.visible).map((x) => x.name) };
    try {
      await api.put("/website/config", body);
      toast.success("Website visibility updated");
      refreshPreview();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); loadAll(); }
  };

  const filtered = rows.filter((r) =>
    [r.product_category, r.product_type, r.service_type, r.part_name].join(" ").toLowerCase().includes(search.toLowerCase())
  );
  const liveCount = preview.reduce((a, c) => a + c.product_types.reduce((b, p) => b + p.services.length, 0), 0);
  const publicUrl = `${API}/public/services`;

  return (
    <>
      <PageHeader
        title="Website Control"
        subtitle="Choose what shows on the public website"
        actions={
          <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="public-feed-link"
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border border-border hover:bg-secondary transition-colors duration-200">
            <ArrowSquareOut size={16} weight="bold" /> View Public Feed
          </a>
        }
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="rounded-sm p-6 border-border" data-testid="stat-live-services">
            <div className="flex items-start justify-between">
              <div><p className="label-caps">Live on Website</p><p className="font-heading text-3xl font-bold mt-2">{liveCount}</p></div>
              <div className="w-10 h-10 rounded-sm bg-green-100 text-green-700 flex items-center justify-center"><Globe size={20} weight="bold" /></div>
            </div>
          </Card>
          <Card className="rounded-sm p-6 border-border" data-testid="stat-visible-categories">
            <div className="flex items-start justify-between">
              <div><p className="label-caps">Visible Categories</p><p className="font-heading text-3xl font-bold mt-2">{config.categories.filter((c) => c.visible).length}/{config.categories.length}</p></div>
              <div className="w-10 h-10 rounded-sm bg-blue-100 text-blue-700 flex items-center justify-center"><Tag size={20} weight="bold" /></div>
            </div>
          </Card>
          <Card className="rounded-sm p-6 border-border" data-testid="stat-visible-service-types">
            <div className="flex items-start justify-between">
              <div><p className="label-caps">Visible Service Types</p><p className="font-heading text-3xl font-bold mt-2">{config.service_types.filter((s) => s.visible).length}/{config.service_types.length}</p></div>
              <div className="w-10 h-10 rounded-sm bg-amber-100 text-amber-700 flex items-center justify-center"><Wrench size={20} weight="bold" /></div>
            </div>
          </Card>
        </div>

        <Tabs defaultValue="visibility">
          <TabsList className="rounded-sm">
            <TabsTrigger value="visibility" className="rounded-sm" data-testid="tab-visibility">Visibility Rules</TabsTrigger>
            <TabsTrigger value="services" className="rounded-sm" data-testid="tab-services">Individual Services</TabsTrigger>
            <TabsTrigger value="preview" className="rounded-sm" data-testid="tab-preview">Website Preview</TabsTrigger>
          </TabsList>

          {/* Visibility Rules */}
          <TabsContent value="visibility" className="pt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="rounded-sm border-border">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2"><Tag size={16} weight="bold" /><h3 className="font-heading font-semibold">Product Categories</h3></div>
              <div className="divide-y divide-border" data-testid="category-visibility-list">
                {config.categories.map((c) => (
                  <div key={c.name} className="flex items-center justify-between px-5 py-3">
                    <span className="font-medium">{c.name}</span>
                    <Switch data-testid={`category-visibility-${c.name.replace(/ /g, "-")}`} checked={c.visible}
                      onCheckedChange={(v) => toggleConfig("category", c.name, v)} />
                  </div>
                ))}
              </div>
            </Card>
            <Card className="rounded-sm border-border">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2"><Wrench size={16} weight="bold" /><h3 className="font-heading font-semibold">Service Types</h3></div>
              <div className="divide-y divide-border" data-testid="service-type-visibility-list">
                {config.service_types.map((s) => (
                  <div key={s.name} className="flex items-center justify-between px-5 py-3">
                    <span className="font-medium">{s.name}</span>
                    <Switch data-testid={`service-type-visibility-${s.name.replace(/ /g, "-")}`} checked={s.visible}
                      onCheckedChange={(v) => toggleConfig("service_type", s.name, v)} />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Individual services */}
          <TabsContent value="services" className="pt-4 space-y-4">
            <div className="relative max-w-sm">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="website-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search services…" className="rounded-sm pl-9" />
            </div>
            <div className="border border-border rounded-sm bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead>Category</TableHead><TableHead>Product Type</TableHead>
                    <TableHead>Service</TableHead><TableHead className="text-right">Amount</TableHead>
                    <TableHead>Payment Mode</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-center">Show</TableHead>
                    <TableHead className="text-center">Featured</TableHead><TableHead className="w-24">Order</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="website-table">
                  {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No services found</TableCell></TableRow>}
                  {filtered.map((r) => {
                    const disabled = r.status !== "active";
                    const mode = r.payment_mode || "full";
                    return (
                      <TableRow key={r.id} className="hover:bg-secondary/30">
                        <TableCell className="font-semibold">{r.product_category}</TableCell>
                        <TableCell>{r.product_type}</TableCell>
                        <TableCell>{r.service_type}{r.part_name ? ` · ${r.part_name}` : ""}</TableCell>
                        <TableCell className="text-right font-mono">{fmtINR(r.amount)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1.5">
                            <Select value={mode} onValueChange={(v) => patchPayment(r.id, v, v === "partial" ? (r.booking_amount || Math.round(r.amount * 0.2)) : 0)}>
                              <SelectTrigger className="rounded-sm h-8 w-[190px]" data-testid={`payment-mode-${r.id}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="full">Full Payment (100%)</SelectItem>
                                <SelectItem value="after">Pay After Service</SelectItem>
                                <SelectItem value="partial">Partial (Booking Amount)</SelectItem>
                              </SelectContent>
                            </Select>
                            {mode === "partial" && (
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-muted-foreground">₹</span>
                                <Input data-testid={`booking-amount-${r.id}`} type="number" defaultValue={r.booking_amount || 0}
                                  onBlur={(e) => { const v = Number(e.target.value); if (v !== (r.booking_amount || 0)) patchPayment(r.id, "partial", v); }}
                                  className="rounded-sm h-8 w-24" placeholder="Booking ₹" />
                                <span className="text-[11px] text-muted-foreground">bal ₹{Math.max(0, (r.amount || 0) - (r.booking_amount || 0))}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell><StatusBadge value={r.status} /></TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Switch data-testid={`website-toggle-${r.id}`} checked={!!r.show_on_website} disabled={disabled}
                              onCheckedChange={(v) => patchRow(r.id, "show_on_website", v)} />
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center">
                            <Switch data-testid={`featured-toggle-${r.id}`} checked={!!r.featured} disabled={disabled || !r.show_on_website}
                              onCheckedChange={(v) => patchRow(r.id, "featured", v)} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input data-testid={`order-input-${r.id}`} type="number" defaultValue={r.display_order || 0}
                            onBlur={(e) => { const v = Number(e.target.value); if (v !== (r.display_order || 0)) patchRow(r.id, "display_order", v); }}
                            className="rounded-sm h-8 w-20" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Preview */}
          <TabsContent value="preview" className="pt-4">
            <Card className="rounded-sm border-border p-6">
              <div className="flex items-center gap-2 mb-6">
                <Eye size={18} weight="bold" />
                <h3 className="font-heading font-semibold">What customers see on the website</h3>
              </div>
              {preview.length === 0 && <p className="text-sm text-muted-foreground">No services are currently visible on the website.</p>}
              <div className="space-y-8" data-testid="website-preview">
                {preview.map((cat) => (
                  <div key={cat.category}>
                    <h4 className="font-heading text-xl font-bold tracking-tight">{cat.category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">
                      {cat.product_types.map((pt) => (
                        <div key={pt.product_type} className="border border-border rounded-sm p-4">
                          <p className="font-semibold mb-3">{pt.product_type}</p>
                          <div className="space-y-2">
                            {pt.services.map((s, i) => (
                              <div key={i} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-0">
                                <span className="flex items-center gap-1.5">
                                  {s.featured && <Star size={13} weight="fill" className="text-amber-500" />}
                                  {s.service_type}{s.part_name ? ` · ${s.part_name}` : ""}
                                </span>
                                <span className="font-mono font-semibold">{fmtINR(s.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
